import type { ReactNode } from 'react';
import { Cluster, Heading, Stack, Surface, Text } from '../ui';

/**
 * Presentational building blocks shared by the two dashboards — the workspace-scoped
 * {@link DashboardPage} (`/app/dashboard`) and the operator-only Platform dashboard
 * (`/app/admin/platform`). Extracted here (#332) so both pages render ONE copy of the
 * hero-stat / titled-card / leaderboard primitives rather than duplicating them.
 */

/** EHE brand blue — the primary data hue, legible in light + dark (matches CostChart). */
export const BRAND_BLUE = '#005ff3';

/** A headline statistic in an overview band. The lead stat is enlarged for emphasis. */
export function HeroStat({
  label,
  value,
  hint,
  emphasis,
  testid,
}: {
  label: string;
  value: string;
  hint: string;
  emphasis?: boolean;
  testid: string;
}) {
  return (
    <Stack gap="1" style={{ minWidth: 0 }}>
      <Text size="sm" tone="muted" weight="medium">
        {label}
      </Text>
      <Heading level={2} size={emphasis ? '3xl' : 'xl'} data-testid={testid}>
        {value}
      </Heading>
      <Text size="xs" tone="secondary">
        {hint}
      </Text>
    </Stack>
  );
}

/** A titled content card used for the breakdown charts. */
export function Section({
  title,
  subtitle,
  testid,
  children,
}: {
  title: string;
  subtitle?: string;
  testid?: string;
  children: ReactNode;
}) {
  return (
    <Surface bordered radius="lg" padding="5" data-testid={testid}>
      <Stack gap="4">
        <Cluster justify="between" align="baseline">
          <Heading level={2} size="sm">
            {title}
          </Heading>
          {subtitle && (
            <Text size="xs" tone="muted">
              {subtitle}
            </Text>
          )}
        </Cluster>
        {children}
      </Stack>
    </Surface>
  );
}

export interface LeaderboardRow {
  id: string;
  primary: string;
  secondary: string;
  value: number;
}

/** A ranked list with pink brand rank pills (dark ink on pink) + a run-count on the right. */
export function Leaderboard({
  rows,
  emptyLabel,
  testid,
}: {
  rows: LeaderboardRow[];
  emptyLabel: string;
  testid: string;
}) {
  if (rows.length === 0) {
    return (
      <Text size="sm" tone="secondary" data-testid={testid}>
        {emptyLabel}
      </Text>
    );
  }
  return (
    <Stack as="ol" gap="3" data-testid={testid} style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {rows.map((row, i) => (
        <Cluster as="li" key={row.id} gap="3" align="center" wrap={false} data-testid="leaderboard-row">
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.75rem',
              height: '1.75rem',
              borderRadius: '999px',
              backgroundColor: '#f09ad7',
              color: '#141414',
              fontWeight: 700,
              fontSize: '0.8125rem',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {i + 1}
          </span>
          <Stack gap="none" style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" weight="medium" truncate>
              {row.primary}
            </Text>
            <Text size="xs" tone="muted" truncate>
              {row.secondary}
            </Text>
          </Stack>
          <Text as="span" size="sm" weight="semibold" mono>
            {row.value.toLocaleString()}
          </Text>
        </Cluster>
      ))}
    </Stack>
  );
}
